import React, { useState, useEffect } from "react";
import {
  Dialog,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import { X } from "lucide-react";
import {
  useCookieConsent,
  CookiePreferences,
} from "../../contexts/CookieConsentContext";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#131313",
      color: "#FFFFFF",
      width: "100%",
      maxWidth: "1077px !important",
      borderRadius: "14px",
      border: "1px solid #27272A",
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.6)",
      overflow: "hidden",
      zIndex: 10000,
      margin: theme.spacing(2),
      position: "relative",
      [theme.breakpoints.up("sm")]: {
        top: 0,
        "@media (min-height: 860px)": {
          top: "-50px",
        },
      },
      [theme.breakpoints.down("xs")]: {
        margin: theme.spacing(1.5),
        maxWidth: "calc(100% - 24px)",
        borderRadius: "12px",
      },
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      backdropFilter: "blur(4px)",
      zIndex: 9999,
    },
  },
  header: {
    padding: "0 24px",
    height: "60px",
    minHeight: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "var(--Black, #0C0A09)",
    borderBottom: "0.2px solid rgba(255, 255, 255, 0.15)",
    boxSizing: "border-box",
  },
  title: {
    color: "#FE5A97",
    fontFamily: "Roboto, sans-serif",
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "100%",
    letterSpacing: "0%",
    verticalAlign: "middle",
    margin: 0,
  },
  closeButton: {
    color: "#71717A",
    padding: "6px",
    borderRadius: "6px",
    transition: "color 0.2s ease, background-color 0.2s ease",
    "&:hover": {
      color: "#FFFFFF",
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
  content: {
    padding: "20px 24px 6px 24px",
    maxHeight: "min(68vh, calc(100vh - 200px))",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#27272A",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#3F3F46",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "16px 16px 6px 16px",
    },
  },
  infoBox: {
    position: "relative",
    backgroundColor: "#1F1F1F",
    borderRadius: "10px",
    padding: "12px 14px",
    marginBottom: "20px",
    boxSizing: "border-box",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "200%",
      height: "200%",
      borderRadius: "20px",
      border: "0.2px solid transparent",
      background:
        "linear-gradient(#1F1F1F, #1F1F1F) padding-box, linear-gradient(90deg, #87959C 1.44%, #89959C 7.69%, #8B949B 13.94%, #B1B3BB 21.15%, #C3C8CC 31.25%, #F1F2F4 38.94%, #D2DBE6 51.44%, #D2DBE6 63.46%, #9AB0B7 81.73%, #A5B7C0 94.71%) border-box",
      transform: "scale(0.5)",
      transformOrigin: "0 0",
      pointerEvents: "none",
      boxSizing: "border-box",
      opacity: 0.55,
    },
  },
  infoText: {
    position: "relative",
    zIndex: 1,
    color: "#A3A3A3",
    fontFamily: "Roboto, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0%",
    margin: 0,
  },
  section: {
    marginBottom: "18px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "6px",
  },
  sectionTitle: {
    color: "#FD3C84",
    fontFamily: '"TT Norms Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 700,
    fontSize: "18px",
    lineHeight: "100%",
    letterSpacing: "0%",
    verticalAlign: "middle",
    margin: 0,
  },
  sectionDesc: {
    color: "#A3A3A3",
    fontFamily: "Roboto, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0%",
    marginTop: 0,
    marginLeft: 0,
    marginRight: 0,
    marginBottom: "10px",
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: "1px solid #27272A",
    borderRadius: "10px",
    padding: "16px 24px",
    minHeight: "56px",
    boxSizing: "border-box",
    marginTop: "8px",
    [theme.breakpoints.down("xs")]: {
      padding: "12px 16px",
      minHeight: "48px",
    },
  },
  toggleLabelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  mobileLockIcon: {
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      display: "inline-flex",
    },
  },
  toggleLabel: {
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 500,
    fontFamily: "Roboto, sans-serif",
    [theme.breakpoints.down("xs")]: {
      fontSize: "14px",
    },
  },
  actions: {
    padding: "20px 24px 24px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "14px",
    [theme.breakpoints.down("xs")]: {
      padding: "14px 16px 18px 16px",
      flexWrap: "wrap",
      gap: "8px",
    },
  },
  outlineBtn: {
    height: "60px",
    backgroundColor: "transparent",
    color: "#FFFFFF",
    border: "1px solid var(--Hot-pink, #FD3C84)",
    borderRadius: "10px",
    padding: "0 28px",
    fontFamily: "Roboto, sans-serif",
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "100%",
    letterSpacing: "0%",
    textAlign: "center",
    verticalAlign: "middle",
    textTransform: "capitalize",
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: "rgba(253, 60, 132, 0.08)",
      borderColor: "#FD3C84",
    },
    [theme.breakpoints.down("xs")]: {
      flex: "1 1 auto",
      height: "50px",
      fontSize: "16px",
      padding: "0 14px",
    },
  },
  primaryBtn: {
    height: "60px",
    backgroundColor: "var(--Hot-pink, #FD3C84)",
    color: "#FFFFFF",
    border: "none",
    borderRadius: "10px",
    padding: "0 36px",
    fontFamily: "Roboto, sans-serif",
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "100%",
    letterSpacing: "0%",
    textAlign: "center",
    verticalAlign: "middle",
    textTransform: "capitalize",
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: "#E03074",
    },
    [theme.breakpoints.down("xs")]: {
      flex: "1 1 100%",
      width: "100%",
      height: "50px",
      fontSize: "16px",
      marginTop: "4px",
      padding: "0 16px",
    },
  },
}));

interface ToggleSwitchProps {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
}

/* ── Custom SVG Icons from Figma ─────────────────────────────────────── */
const ShieldCookieIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M11.467 20.8209C11.6202 20.9375 11.8075 21.0007 12 21.0007C12.1926 21.0007 12.3798 20.9375 12.533 20.8209C14.168 19.5939 19 15.5869 19 11.0169V6.08692C19.001 5.95975 18.9549 5.83672 18.8705 5.7416C18.7861 5.64648 18.6694 5.58602 18.543 5.57192C16.5407 5.36683 14.6216 4.66343 12.961 3.52592L12.351 3.10892C12.2477 3.03798 12.1253 3 12 3C11.8747 3 11.7523 3.03798 11.649 3.10892L11.039 3.52592C9.37843 4.66343 7.45936 5.36683 5.45702 5.57192C5.33063 5.58602 5.21395 5.64648 5.12954 5.7416C5.04513 5.83672 4.99899 5.95975 5.00002 6.08692V11.0169C5.00002 15.5869 9.83202 19.5939 11.467 20.8189"
      stroke="#FD3C84"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AnalyticsCookieIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M6 21V16C6 15.7348 6.10536 15.4804 6.29289 15.2929C6.48043 15.1054 6.73478 15 7 15C7.26522 15 7.51957 15.1054 7.70711 15.2929C7.89464 15.4804 8 15.7348 8 16V21C8 21.2652 7.89464 21.5196 7.70711 21.7071C7.51957 21.8946 7.26522 22 7 22C6.73478 22 6.48043 21.8946 6.29289 21.7071C6.10536 21.5196 6 21.2652 6 21ZM11 21V9C11 8.73478 11.1054 8.48043 11.2929 8.29289C11.4804 8.10536 11.7348 8 12 8C12.2652 8 12.5196 8.10536 12.7071 8.29289C12.8946 8.48043 13 8.73478 13 9V21C13 21.2652 12.8946 21.5196 12.7071 21.7071C12.5196 21.8946 12.2652 22 12 22C11.7348 22 11.4804 21.8946 11.2929 21.7071C11.1054 21.5196 11 21.2652 11 21ZM16 21V3C16 2.73478 16.1054 2.48043 16.2929 2.29289C16.4804 2.10536 16.7348 2 17 2C17.2652 2 17.5196 2.10536 17.7071 2.29289C17.8946 2.48043 18 2.73478 18 3V21C18 21.2652 17.8946 21.5196 17.7071 21.7071C17.5196 21.8946 17.2652 22 17 22C16.7348 22 16.4804 21.8946 16.2929 21.7071C16.1054 21.5196 16 21.2652 16 21Z"
      fill="#FD3C84"
    />
  </svg>
);

const FunctionalCookieIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M12.058 21.9996C16.973 21.9996 21.064 18.4936 21.942 13.8586C22.2 12.4966 21.593 12.5686 20.31 12.3436C19.4 12.1836 18.55 11.5666 18.233 10.5876C18.018 9.92162 17.938 9.89462 17.235 9.86562C15.786 9.80562 14.625 8.26862 15.203 7.02562C15.419 6.56162 15.412 6.50862 15.008 6.19362C14.301 5.64362 13.971 4.61662 14.098 3.62562C14.235 2.55862 14.315 2.17462 13.185 2.05462C7.457 1.44462 2 6.01962 2 11.9946C2 17.5196 6.503 21.9996 12.058 21.9996Z"
      stroke="#FD3C84"
      strokeWidth="1.5"
    />
    <path
      d="M11.0061 12H10.9961M6.00609 10H5.99609M12.0061 18H11.9961"
      stroke="#FD3C84"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 6L9 7M17 14L16 15M7 15L8 16"
      stroke="#FD3C84"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AdvertisingCookieIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.00066 9C5.00066 7.14348 5.73816 5.36301 7.05092 4.05025C8.36367 2.7375 10.1441 2 12.0007 2C13.8572 2 15.6377 2.7375 16.9504 4.05025C18.2632 5.36301 19.0007 7.14348 19.0007 9V12.764L20.8227 16.408C20.9065 16.5757 20.9461 16.7621 20.9377 16.9494C20.9293 17.1368 20.8731 17.3188 20.7745 17.4783C20.6759 17.6379 20.5382 17.7695 20.3744 17.8608C20.2106 17.9521 20.0262 18 19.8387 18H15.8747C15.6522 18.8582 15.1511 19.6183 14.4499 20.1609C13.7488 20.7035 12.8873 20.9979 12.0007 20.9979C11.1141 20.9979 10.2526 20.7035 9.5514 20.1609C8.85023 19.6183 8.3491 18.8582 8.12666 18H4.16266C3.97514 18 3.79072 17.9521 3.62693 17.8608C3.46314 17.7695 3.3254 17.6379 3.22681 17.4783C3.12822 17.3188 3.07204 17.1368 3.06361 16.9494C3.05518 16.7621 3.09479 16.5757 3.17866 16.408L5.00066 12.764V9ZM10.2687 18C10.4442 18.304 10.6967 18.5565 11.0007 18.732C11.3047 18.9075 11.6496 18.9999 12.0007 18.9999C12.3517 18.9999 12.6966 18.9075 13.0006 18.732C13.3046 18.5565 13.5571 18.304 13.7327 18H10.2687ZM12.0007 4C10.6746 4 9.40281 4.52678 8.46513 5.46447C7.52745 6.40215 7.00066 7.67392 7.00066 9V12.764C7.00064 13.0743 6.9284 13.3804 6.78966 13.658L5.61966 16H18.3827L17.2127 13.658C17.0736 13.3805 17.001 13.0744 17.0007 12.764V9C17.0007 7.67392 16.4739 6.40215 15.5362 5.46447C14.5985 4.52678 13.3267 4 12.0007 4Z"
      fill="#FD3C84"
    />
  </svg>
);

const CookieLockIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M6.66797 8.33333V6.66667C6.66797 4.36583 7.70047 2.5 10.0013 2.5C12.3021 2.5 13.3346 4.36583 13.3346 6.66667V8.33333M13.3346 11.6667V14.1667M2.91797 14.8333V11C2.91797 10.0667 2.91797 9.6 3.09964 9.24417C3.25928 8.93027 3.51425 8.675 3.82797 8.515C4.18464 8.33417 4.6513 8.33417 5.58464 8.33417H14.418C15.3513 8.33417 15.818 8.33417 16.1746 8.515C16.4882 8.67479 16.7432 8.92974 16.903 9.24333C17.0846 9.6 17.0846 10.0667 17.0846 11V14.8333C17.0846 15.7667 17.0846 16.2333 16.903 16.59C16.7432 16.9036 16.4882 17.1585 16.1746 17.3183C15.818 17.5 15.3513 17.5 14.418 17.5H5.58464C4.6513 17.5 4.18464 17.5 3.82797 17.3183C3.51438 17.1585 3.25942 16.9036 3.09964 16.59C2.91797 16.2342 2.91797 15.7675 2.91797 14.8333Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  disabled = false,
}) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      style={{
        width: 42,
        height: 24,
        borderRadius: 50,
        backgroundColor: checked ? "#FD3C84" : "#747474",
        border: "none",
        outline: "none",
        position: "relative",
        cursor: disabled ? "default" : "pointer",
        padding: 0,
        transition: "background-color 0.2s ease",
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          backgroundColor: "#FFFFFF",
          position: "absolute",
          top: 3,
          left: checked ? 21 : 3,
          transition: "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.35)",
        }}
      />
    </button>
  );
};

interface CookiePreferencesDialogProps {
  open: boolean;
  onClose: () => void;
}

const CookiePreferencesDialog: React.FC<CookiePreferencesDialogProps> = ({
  open,
  onClose,
}) => {
  const classes = useStyles();
  const { preferences, savePreferences, rejectAll } = useCookieConsent();
  const [localPreferences, setLocalPreferences] =
    useState<CookiePreferences>(preferences);

  useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  const handlePreferenceChange =
    (key: keyof CookiePreferences) => (checked: boolean) => {
      if (key === "essential") {
        return;
      }

      setLocalPreferences((prev) => ({
        ...prev,
        [key]: checked,
      }));
    };

  const handleSave = () => {
    savePreferences(localPreferences);
    onClose();
  };

  const handleCancel = () => {
    setLocalPreferences(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      functional: true,
      advertising: true,
    };
    setLocalPreferences(allAccepted);
    savePreferences(allAccepted);
    onClose();
  };

  const handleRejectAll = () => {
    rejectAll();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      className={classes.dialog}
      maxWidth={false}
      fullWidth
    >
      <div className={classes.header}>
        <h2 className={classes.title}>Cookie Preference</h2>
        <IconButton
          className={classes.closeButton}
          onClick={handleCancel}
          size="small"
          aria-label="Close"
        >
          <X size={18} />
        </IconButton>
      </div>

      <div className={classes.content}>
        <div className={classes.infoBox}>
          <p className={classes.infoText}>
            We use cookies to enhance your experience and analyze site usage.
            You can customize your preferences below. Essential cookies are
            required for the site to function and cannot be disabled.
          </p>
        </div>

        {/* 1. Essential Cookies */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <ShieldCookieIcon size={20} />
            <h3 className={classes.sectionTitle}>Essential Cookies</h3>
          </div>
          <p className={classes.sectionDesc}>
            Required for the website to function properly. These include
            authentication, security, and basic navigation features.
          </p>
          <div className={classes.toggleRow}>
            <div className={classes.toggleLabelContainer}>
              <span className={classes.mobileLockIcon}>
                <CookieLockIcon size={20} />
              </span>
              <span className={classes.toggleLabel}>
                Essential Cookies (Required)
              </span>
            </div>
            <ToggleSwitch checked={true} disabled={true} />
          </div>
        </div>

        {/* 2. Analytics Cookies */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <AnalyticsCookieIcon size={20} />
            <h3 className={classes.sectionTitle}>Analytics Cookies</h3>
          </div>
          <p className={classes.sectionDesc}>
            Help us understand how visitors interact with our website by
            collecting and reporting information anonymously. Used by Google
            Analytics and Firebase Analytics.
          </p>
          <div className={classes.toggleRow}>
            <div className={classes.toggleLabelContainer}>
              <span className={classes.mobileLockIcon}>
                <CookieLockIcon size={20} />
              </span>
              <span className={classes.toggleLabel}>Analytics Cookies</span>
            </div>
            <ToggleSwitch
              checked={localPreferences.analytics}
              onChange={handlePreferenceChange("analytics")}
            />
          </div>
        </div>

        {/* 3. Functional Cookies */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <FunctionalCookieIcon size={20} />
            <h3 className={classes.sectionTitle}>Functional Cookies</h3>
          </div>
          <p className={classes.sectionDesc}>
            Enable enhanced functionality and personalization, such as
            remembering your preferences and login state.
          </p>
          <div className={classes.toggleRow}>
            <div className={classes.toggleLabelContainer}>
              <span className={classes.mobileLockIcon}>
                <CookieLockIcon size={20} />
              </span>
              <span className={classes.toggleLabel}>Functional Cookies</span>
            </div>
            <ToggleSwitch
              checked={localPreferences.functional}
              onChange={handlePreferenceChange("functional")}
            />
          </div>
        </div>

        {/* 4. Advertising Cookies */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <AdvertisingCookieIcon size={20} />
            <h3 className={classes.sectionTitle}>Advertising Cookies</h3>
          </div>
          <p className={classes.sectionDesc}>
            Used to track visitors across websites to display relevant and
            engaging advertisements. Currently not used but may be added in
            future updates.
          </p>
          <div className={classes.toggleRow}>
            <div className={classes.toggleLabelContainer}>
              <span className={classes.mobileLockIcon}>
                <CookieLockIcon size={20} />
              </span>
              <span className={classes.toggleLabel}>Advertising Cookies</span>
            </div>
            <ToggleSwitch
              checked={localPreferences.advertising}
              onChange={handlePreferenceChange("advertising")}
            />
          </div>
        </div>
      </div>

      <div className={classes.actions}>
        <button
          type="button"
          className={classes.outlineBtn}
          onClick={handleRejectAll}
        >
          Reject All
        </button>
        <button
          type="button"
          className={classes.outlineBtn}
          onClick={handleAcceptAll}
        >
          Accept All
        </button>
        <button
          type="button"
          className={classes.primaryBtn}
          onClick={handleSave}
        >
          Save Preference
        </button>
      </div>
    </Dialog>
  );
};

export default CookiePreferencesDialog;
